import type { MeaningCard, VocabularySnapshot, WordEntry, WordType } from "./types.ts";

export interface GroupedWord {
  word: WordEntry;
  meanings: MeaningCard[];
  types: WordType[];
  meaningCount: number;
  latestMeaningAt: string;
}

export function meaningsForWord(
  snapshot: VocabularySnapshot,
  wordId: string,
): MeaningCard[] {
  return snapshot.meanings.filter((meaning) => meaning.wordId === wordId);
}

export function findWordByNormalizedText(
  snapshot: VocabularySnapshot,
  normalizedText: string,
): WordEntry | undefined {
  return snapshot.words.find((word) => word.normalizedText === normalizedText);
}

export function findWordById(
  snapshot: VocabularySnapshot,
  wordId: string,
): WordEntry | undefined {
  return snapshot.words.find((word) => word.id === wordId);
}

export function findMeaningById(
  snapshot: VocabularySnapshot,
  meaningId: string,
): MeaningCard | undefined {
  return snapshot.meanings.find((meaning) => meaning.id === meaningId);
}

export function distinctTypes(meanings: readonly MeaningCard[]): WordType[] {
  const seen = new Set<WordType>();
  const types: WordType[] = [];
  for (const meaning of meanings) {
    if (!seen.has(meaning.type)) {
      seen.add(meaning.type);
      types.push(meaning.type);
    }
  }
  return types;
}

export function latestMeaningTimestamp(meanings: readonly MeaningCard[]): string {
  return meanings.reduce((latest, meaning) => {
    return meaning.createdAt > latest ? meaning.createdAt : latest;
  }, "");
}

export function groupWords(snapshot: VocabularySnapshot): GroupedWord[] {
  return snapshot.words
    .map((word) => {
      const meanings = meaningsForWord(snapshot, word.id);
      return {
        word,
        meanings,
        types: distinctTypes(meanings),
        meaningCount: meanings.length,
        latestMeaningAt: latestMeaningTimestamp(meanings) || word.createdAt,
      };
    })
    .filter((group) => group.meaningCount > 0)
    .sort((a, b) => {
      if (a.latestMeaningAt === b.latestMeaningAt) {
        return a.word.normalizedText.localeCompare(b.word.normalizedText);
      }
      return a.latestMeaningAt < b.latestMeaningAt ? 1 : -1;
    });
}

export function searchGroups(groups: readonly GroupedWord[], query: string): GroupedWord[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return [...groups];
  }
  return groups.filter((group) => group.word.normalizedText.includes(needle));
}

export function filterGroupsByTypes(
  groups: readonly GroupedWord[],
  types: readonly WordType[],
): GroupedWord[] {
  if (types.length === 0) {
    return [...groups];
  }
  const wanted = new Set(types);
  return groups.filter((group) => group.meanings.some((meaning) => wanted.has(meaning.type)));
}

export type WordOpenTarget =
  | { kind: "card"; meaningId: string; wordId: string }
  | { kind: "list"; wordId: string };

export function navigationForWord(meanings: readonly MeaningCard[]): WordOpenTarget | null {
  if (meanings.length === 0) {
    return null;
  }
  if (meanings.length === 1) {
    return { kind: "card", meaningId: meanings[0].id, wordId: meanings[0].wordId };
  }
  return { kind: "list", wordId: meanings[0].wordId };
}
